import { useEffect } from "react";

import { meldWeergave } from "./publicApi";

/** De twee sites waarop een dier kan staan. Het telpunt wil weten op welke er gekeken is. */
export type Site = "DIERMATCH" | "IKZOEKBAAS";

/** Een etmaal: het venster waarin dezelfde bezoeker maar één keer voor hetzelfde dier telt. */
const TELVENSTER_MS = 24 * 60 * 60 * 1000;

/** Hoe lang de pagina zichtbaar moet blijven voordat het een weergave heet. */
const KIJKTIJD_MS = 5000;

/**
 * De sleutel in localStorage, per site. ikzoekbaas had `izb-gezien:` al voordat DierMatch meetelde;
 * die blijft letterlijk zo, anders begint het venster van iedere bezoeker opnieuw.
 */
function sleutelVan(site: Site, slug: string): string {
  return `${site === "DIERMATCH" ? "dm" : "izb"}-gezien:${slug}`;
}

/**
 * Is dit dier door deze browser al geteld, en nog binnen het venster van een dag?
 *
 * <p>Alles wat misgaat telt als "nee": in een afgeschermde modus gooit `localStorage` bij het lezen,
 * en dan is één weergave te veel beter dan een pagina die stukloopt op een teller.
 */
function alGeteld(sleutel: string): boolean {
  try {
    const t = Number(globalThis.localStorage?.getItem(sleutel));
    return Number.isFinite(t) && t > 0 && Date.now() - t < TELVENSTER_MS;
  } catch {
    return false;
  }
}

/** Vastleggen dat er zojuist geteld is. Lukt dat niet, dan telt deze bezoeker morgen gewoon opnieuw. */
function noteerGeteld(sleutel: string): void {
  try {
    globalThis.localStorage?.setItem(sleutel, String(Date.now()));
  } catch {
    /* afgeschermde modus: niets te bewaren, niets aan de hand */
  }
}

/**
 * Tel deze weergave — maar alleen als er echt iemand kijkt.
 *
 * <p>Drie voorwaarden, en alle drie doen werk. De pagina moet ZICHTBAAR zijn (een tab die op de
 * achtergrond is voorgeladen telt niet), hij moet dat VIJF SECONDEN blijven (een crawler wacht
 * nergens op, en wie meteen wegklikt heeft het dier niet bekeken), en het mag maar ÉÉN KEER per
 * bezoeker per dier per DAG (anders telt verversen mee, en dan meet je iemands twijfel).
 *
 * <p>Dat venster van een dag zit in {@code localStorage} en niet in {@code sessionStorage}: die
 * laatste geldt per tabblad, dus wie hetzelfde dier in een tweede tabblad opende telde twee keer,
 * en wie morgen terugkomt om er nog eens over na te denken telde nooit meer mee. Per browser per
 * dag is allebei niet waar. Het is bewust geen cookie: er wordt niets over deze bezoeker bewaard,
 * niet hier en niet op de server. Het enige wat blijft is een teller die omhoog ging.
 *
 * <p><b>Waarom gedeeld tussen de twee sites.</b> Dit stond eerst alleen in ikzoekbaas; DierMatch
 * telde helemaal niet, en daardoor stond er in de backoffice bij DierMatch een cijfer dat van de
 * andere site kwam (Maarten, 30-10-2026). Twee keer dezelfde zeven uitschrijven is twee keer de
 * kans dat er eentje wegzakt, en het zijn precies de regels die bepalen of het woord "bekeken" nog
 * iets betekent.
 *
 * @param slug het dier dat nu in beeld is; `null` of leeg telt niet
 * @param site waar gekeken wordt — dat is wat de backoffice per overzicht laat zien
 */
export function useTelWeergave(slug: string | null | undefined, site: Site): void {
  useEffect(() => {
    if (!slug) {
      return undefined;
    }
    const sleutel = sleutelVan(site, slug);
    if (alGeteld(sleutel)) {
      return undefined;
    }
    let tijd: ReturnType<typeof setTimeout> | undefined;
    const start = () => {
      if (tijd !== undefined || document.visibilityState !== "visible") {
        return;
      }
      tijd = setTimeout(() => {
        noteerGeteld(sleutel);
        void meldWeergave(slug, site);
      }, KIJKTIJD_MS);
    };
    const stop = () => {
      if (document.visibilityState !== "visible" && tijd !== undefined) {
        clearTimeout(tijd);
        tijd = undefined;
      }
    };
    start();
    document.addEventListener("visibilitychange", stop);
    document.addEventListener("visibilitychange", start);
    return () => {
      if (tijd !== undefined) clearTimeout(tijd);
      document.removeEventListener("visibilitychange", stop);
      document.removeEventListener("visibilitychange", start);
    };
  }, [slug, site]);
}
